import React, { useEffect, useLayoutEffect, useState } from "react";
import icons from "../../../../../assets/images/icons";
import { getPhotoUrl } from "../../../../../common/utils/getPhotoUrl";
import { E_RAND_TYPE_LABEL, DEAL_TYPE_NAMES } from "../../../../../common/constants/saleConstants";
import { realPriceColor } from "../../../../../utils/color";
import { priceFormat } from "../../../../../utils/format";
import { getPropertyBySuid } from "../../../apis/chatApi";
import ChatPropertyCard from "../ChatPropertyCard/ChatPropertyCard";
import styles from "./ChatMessage.module.scss";
import MessageImageGrid from "./MessageImageGrid";

// estate_type → realPriceColor에 넣을 문자열
const ESTATE_TYPE_TAG = {
  0: "단독", 1: "토지", 2: "다세대", 3: "아파트", 4: "오피스텔",
  5: "상업용건물", 6: "주상복합", 7: "상가사무실", 8: "공장/창고",
  9: "원룸", 10: "다가구", 12: "숙박시설", 90: "분양권", 91: "입주권", 99: "기타",
};

function formatSalePrice(property) {
  const { deal_type, price, deposit } = property;
  if (deal_type === 1 && deposit) return priceFormat(deposit);
  return price ? priceFormat(price) : "";
}

function SaleContentCard({ suid, onSaleCardClick }) {
  const [property, setProperty] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPropertyBySuid(suid).then((data) => {
      if (!cancelled && data) setProperty(data);
    });
    return () => {
      cancelled = true;
    };
  }, [suid]);

  // 카드가 렌더링될 때 스크롤 컨테이너에게 알림 (paint 전 동기 실행)
  useLayoutEffect(() => {
    if (property) {
      window.dispatchEvent(new CustomEvent("messenger:saleCardReady"));
    }
  }, [property]);

  if (!property) return null;

  const thumbnailUrl = property.photo ? getPhotoUrl(property.photo, "building_lg") : null;
  const dealTypeName = DEAL_TYPE_NAMES[property.deal_type] ?? "매매";
  const isMonthly = property.deal_type === 2;
  const depositPrice = isMonthly ? priceFormat(property.deposit || 0) : undefined;
  const monthlyPrice = isMonthly ? priceFormat(property.monthly || 0) : undefined;
  const displayPrice = isMonthly ? "" : formatSalePrice(property);

  return (
    <ChatPropertyCard
      address={property.address || ""}
      transactionType={dealTypeName}
      price={displayPrice}
      depositPrice={depositPrice}
      monthlyPrice={monthlyPrice}
      thumbnailUrl={thumbnailUrl}
      tag={E_RAND_TYPE_LABEL[property.e_rand_type]}
      tagStyle={realPriceColor(ESTATE_TYPE_TAG[property.estate_type])}
      ownerVerified={!!property.owner_verified}
      onClick={onSaleCardClick ? () => onSaleCardClick(suid) : undefined}
    />
  );
}

function SharedContentBlock({ content, onSaleCardClick }) {
  if (content.contentType === "sale" && content.contentId) {
    return <SaleContentCard suid={content.contentId} onSaleCardClick={onSaleCardClick} />;
  }
  return (
    <div className={styles.contentCard} data-part="shared-content">
      <span className={styles.contentCardLabel}>콘텐츠</span>
      <span className={styles.contentCardType}>{content.contentType}</span>
      <span className={styles.contentCardId} title={content.contentId}>
        {content.contentId}
      </span>
    </div>
  );
}

function FileAttachmentList({ fileRows, onDownloadFile, onDownload }) {
  return (
    <div className={styles.fileCards}>
      {fileRows.map((row, idx) => {
        const canDownload = Boolean(row.fileUrl);
        const onDownloadClick = () => {
          if (!row.fileUrl) return;
          if (onDownloadFile) onDownloadFile(row.fileUrl, row.fileName ?? undefined);
          else if (fileRows.length === 1) onDownload?.();
        };
        return (
          <div
            key={row.fileUrl ?? `${row.fileName}-${idx}`}
            className={styles.fileCard}
            data-node-id={idx === 0 ? "40004313:34227" : undefined}
          >
            <div className={styles.fileIconWrap} aria-hidden>
              {icons.messager?.fileIcon ? (
                <img
                  src={icons.messager.fileIcon}
                  alt=""
                  className={styles.fileIcon}
                  width={18}
                  height={18}
                />
              ) : (
                <span className={styles.fileIconFallback}>📄</span>
              )}
            </div>
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{row.fileName ?? "파일명"}</span>
              <span className={styles.fileExtension}>
                {row.fileExtension ?? "파일 확장자 (PDF, 이미지...)"}
              </span>
            </div>
            <button
              type="button"
              className={styles.downloadBtn}
              onClick={onDownloadClick}
              disabled={!canDownload}
              aria-label={`${row.fileName ?? "파일"} 다운받기`}
            >
              {icons.messager?.download && (
                <img
                  src={icons.messager.download}
                  alt=""
                  className={styles.downloadIcon}
                  width={18}
                  height={18}
                />
              )}
              <span className={styles.downloadBtnLabel}>다운받기</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function TextBubble({ body }) {
  return (
    <div className={styles.bubble}>
      <div className={styles.body}>{body}</div>
    </div>
  );
}

/**
 * 말풍선 본문: 공유 콘텐츠 → 이미지 그리드 → 파일 카드 → 텍스트
 */
export default function ChatMessageBody({
  hasContent,
  content,
  resolvedUrls,
  resolvedThumbnailUrls,
  resolvedImageNames,
  onImageClick,
  imageFallbackSrc,
  hasFile,
  fileRows,
  onDownload,
  onDownloadFile,
  body,
  onSaleCardClick,
}) {
  return (
    <>
      {hasContent && <SharedContentBlock content={content} onSaleCardClick={onSaleCardClick} />}
      {resolvedUrls.length > 0 && (
        <MessageImageGrid
          urls={resolvedUrls}
          thumbnailUrls={resolvedThumbnailUrls}
          fileNames={resolvedImageNames}
          onImageClick={onImageClick}
          fallbackSrc={imageFallbackSrc}
        />
      )}
      {hasFile && (
        <FileAttachmentList
          fileRows={fileRows}
          onDownloadFile={onDownloadFile}
          onDownload={onDownload}
        />
      )}
      {body != null && body !== "" && <TextBubble body={body} />}
    </>
  );
}
